"use client";

import { useState, useEffect, useRef } from "react";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { DayPicker, DateRange } from "react-day-picker";
import "react-day-picker/style.css";

// ─────────────────────────────────────────────────────────
// DARK-MODE AWARE RANGE CALENDAR STYLES
// ─────────────────────────────────────────────────────────
const rangeCalendarStyles = (isMobile: boolean) => `
   .rdp {
 --rdp-cell-size: ${isMobile ? "34px" : "40px"};
  --rdp-accent-color: #7c3aed;
  margin: 0;
  font-size: ${isMobile ? "0.8rem" : "0.9rem"};
    background: transparent !important;
    color: inherit;
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
  }
  .rdp *::selection {
    background: transparent !important;
    color: inherit !important;
  }
  .rdp *::-moz-selection {
    background: transparent !important;
    color: inherit !important;
  }
  .rdp-months, .rdp-month, .rdp-table, .rdp-tbody,
  .rdp-tfoot, .rdp-thead, .rdp-row, .rdp-cell {
    background: transparent !important;
    background-color: transparent !important;
  }
  .rdp-month {
    position: relative;
  }
  .rdp-caption,
  .rdp-month_caption {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 0 0.5rem 0;
    min-height: 28px;
  }
 .rdp-caption_label {
    font-size: 1rem;
    font_weight: 700;
  }
  .rdp-nav {
    position: absolute !important;
    top: 0;
    left: 0;
    right: 0;
    height: 28px;
    display: flex !important;
    justify-content: space-between;
    align-items: center;
    pointer-events: none;
    z-index: 1;
  }
  .rdp-button_previous, .rdp-button_next {
    pointer-events: auto;
    width: 26px; height: 26px;
    border-radius: 9999px;
    border: 1px solid currentColor;
    opacity: 0.25;
    display: flex; align-items: center; justify-content: center;
    transition: background-color 0.15s, opacity 0.15s;
    color: inherit;
  }
  .rdp-button_previous:hover, .rdp-button_next:hover {
    opacity: 0.6;
    background-color: rgba(124, 58, 237, 0.12) !important;
  }
  .rdp-button_previous[disabled], .rdp-button_next[disabled] {
    opacity: 0.1; cursor: not-allowed;
  }
  .rdp-button_previous[disabled]:hover, .rdp-button_next[disabled]:hover {
    background-color: transparent !important;
  }
 .rdp-months {
    display: flex;
    gap: ${isMobile ? "0.5rem" : "2rem"};
    flex-direction: ${isMobile ? "column" : "row"};
    width: ${isMobile ? "100%" : "max-content"};
  }
  .rdp-month { width: ${isMobile ? "100%" : "max-content"}; }
  .rdp-table { ${isMobile ? "width: 100%; max-width: 100%;" : ""} border-collapse: separate !important; border-spacing: 3px 5px !important; }    font-weight: 600; color: inherit; opacity: 0.4;    padding: ${isMobile ? "0.2rem 0" : "0.3rem 0"};
    text-transform: none;
    letter-spacing: 0;
  }
     .rdp-day { border-radius: 0 !important; transition: background 0.12s, color 0.12s; font-size: inherit; }
  .rdp-day_button {
    background: transparent !important;
    border: none !important;
    color: inherit !important;
    width: 100%;
    height: 100%;
    border-radius: 6px;
  }
  .rdp-day_range_start .rdp-day_button,
  .rdp-day_range_end   .rdp-day_button {
    background: #4c1d95 !important; color: #ffffff !important;
    font-weight: 700; border-radius: 6px !important;
  }
  .rdp-day_range_middle { background: rgba(124,58,237,0.12) !important; }
  .rdp-day_range_middle .rdp-day_button {
    color: inherit !important;
    font-weight: 500;
    border-radius: 6px !important;
  }
  .rdp-day:not(.rdp-day_disabled):not(.rdp-day_range_start):not(.rdp-day_range_end) .rdp-day_button:hover {
    background: rgba(124,58,237,0.18) !important; color: inherit !important;
  }
  .rdp-day_today:not(.rdp-day_range_start):not(.rdp-day_range_end):not(.rdp-day_range_middle) .rdp-day_button {
    background: rgba(124, 58, 237, 0.1) !important;
    font-weight: 600;
    color: inherit !important;
  }
  .rdp-day_disabled .rdp-day_button { opacity: 0.25 !important; cursor: not-allowed; }
  .rdp-weekday { color: inherit; opacity: 0.4; font-weight: 600; }
`;

function toISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function CalendarDropdown({
  checkIn,
  checkOut,
  onCheckInChange,
  onCheckOutChange,
  transparent = true,
}: {
  checkIn: string;
  checkOut: string;
  onCheckInChange: (v: string) => void;
  onCheckOutChange: (v: string) => void;
  transparent?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isMobile, setIsMobile] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setCurrentMonth(checkIn ? new Date(checkIn) : new Date());
      setTimeout(() => {
        ref.current
          ?.querySelector<HTMLElement>("[data-date-picker-container]")
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 50);
    }
  }, [open]);

  const formatShort = (iso?: string) =>
    iso
      ? new Date(iso)
          .toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
          .toUpperCase()
      : "";

  const formatFooter = (iso?: string) =>
    iso
      ? new Date(iso).toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
        })
      : "";

  const nightCount =
    checkIn && checkOut
      ? Math.round(
          (new Date(checkOut).getTime() - new Date(checkIn).getTime()) /
            86_400_000,
        )
      : 0;

  const rangeValue: DateRange = {
    from: checkIn ? new Date(checkIn) : undefined,
    to: checkOut ? new Date(checkOut) : undefined,
  };

  const stepLabel = !checkIn
    ? "Select your check-in date"
    : !checkOut
      ? "Now select your check-out date"
      : `${formatShort(checkIn)} → ${formatShort(checkOut)} · ${nightCount} night${nightCount !== 1 ? "s" : ""}`;

  const stepColor = !checkIn
    ? "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700"
    : !checkOut
      ? "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400 border-amber-100 dark:border-amber-900/40"
      : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900/40";

  return (
    <div className="relative w-full isolate" ref={ref}>
      {/* ── Trigger ── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={[
          "w-full flex items-center gap-2.5 px-3.5 h-11",
          transparent
            ? "bg-white/10 dark:bg-zinc-900/20 hover:bg-white/20 dark:hover:bg-zinc-900/30"
            : "bg-transparent hover:bg-gray-50 dark:hover:bg-zinc-800",
          "ring-1 rounded-full text-left transition-all duration-150 outline-none",
          open
            ? "ring-violet-400/70 dark:ring-violet-500/50"
            : transparent
              ? "ring-white/30 dark:ring-white/10 hover:ring-white/50 dark:hover:ring-white/20"
              : "ring-gray-300 dark:ring-zinc-700 hover:ring-gray-400 dark:hover:ring-zinc-600",
        ].join(" ")}
      >
        <Calendar
          className={`w-4 h-4 flex-shrink-0 transition-colors ${
            open
              ? "text-violet-500"
              : transparent
                ? "text-white dark:text-white"
                : "text-gray-500 dark:text-zinc-400"
          }`}
        />
        <span className="flex-1 text-sm whitespace-nowrap overflow-hidden">
          {checkIn ? (
            <>
              <span className="text-zinc-900 dark:text-zinc-100 font-medium">
                {formatShort(checkIn)}
              </span>
              <span className="text-zinc-400 dark:text-zinc-600 mx-1.5">-</span>
              {checkOut ? (
                <span className="text-zinc-900 dark:text-zinc-100 font-medium">
                  {formatShort(checkOut)}
                </span>
              ) : (
                <span className="text-zinc-400 dark:text-zinc-500 italic">
                  Pick check-out
                </span>
              )}
            </>
          ) : (
            <span
              className={
                transparent
                  ? "text-white dark:text-white"
                  : "text-gray-400 dark:text-zinc-500"
              }
            >
              CHECK-IN — CHECK-OUT
            </span>
          )}
        </span>

        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 text-zinc-400 dark:text-zinc-500 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* ── Dropdown panel ── */}
      {open && (
        <div
          data-date-picker-container
          role="dialog"
          aria-label="Select check-in and check-out dates"
          className={[
            "absolute z-[9999] mt-2",
            "bg-white dark:bg-zinc-900",
            "border border-zinc-200 dark:border-zinc-700",
            "rounded-2xl shadow-xl shadow-zinc-900/10 dark:shadow-zinc-950/60",
            "overflow-hidden",
            isMobile
              ? "left-0 right-0 max-h-[75vh] overflow-y-auto"
              : "left-0 top-full w-max max-h-[80vh] overflow-y-auto",
          ].join(" ")}
        >
          {/* Panel header — minimal clear/close, no colored status */}
          {(checkIn || checkOut) && (
            <div className="flex items-center justify-end px-4 py-2 border-b border-zinc-100 dark:border-zinc-800">
              <button
                onClick={() => {
                  onCheckInChange("");
                  onCheckOutChange("");
                }}
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-100 font-medium px-2 py-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            </div>
          )}

          {/* Calendar — inherits text color from wrapper div */}
          <div className="p-4 bg-transparent text-zinc-900 dark:text-zinc-100">
            {" "}
            <style
              dangerouslySetInnerHTML={{
                __html: rangeCalendarStyles(isMobile),
              }}
            />
            <DayPicker
              mode="range"
              selected={rangeValue}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              numberOfMonths={isMobile ? 1 : 2}
              fromMonth={
                new Date(new Date().getFullYear(), new Date().getMonth(), 1)
              }
              components={{
                Chevron: ({ orientation }) =>
                  orientation === "left" ? (
                    <ChevronLeft className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  ),
              }}
              onSelect={(range: DateRange | undefined) => {
                if (!range) {
                  onCheckInChange("");
                  onCheckOutChange("");
                  return;
                }
                if (
                  range.from &&
                  (!range.to || range.from.getTime() === range.to.getTime())
                ) {
                  onCheckInChange(toISO(range.from));
                  onCheckOutChange("");
                  return;
                }
                if (range.from && range.to) {
                  onCheckInChange(toISO(range.from));
                  onCheckOutChange(toISO(range.to));
                  if (isMobile) setOpen(false);
                }
              }}
              disabled={(date) => {
                const MAX_STAY = 60;
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);
                const todayMidnight = new Date();
                todayMidnight.setHours(0, 0, 0, 0);
                if (d < todayMidnight) return true;
                if (checkIn && !checkOut) {
                  const ci = new Date(checkIn);
                  ci.setHours(0, 0, 0, 0);
                  if (d <= ci) return true;
                  const maxOut = new Date(ci);
                  maxOut.setDate(maxOut.getDate() + MAX_STAY);
                  if (d > maxOut) return true;
                }
                return false;
              }}
              footer={null}
            />
          </div>

          {/* Footer — plain status text, matches reference style */}
          <div className="border-t border-zinc-100 dark:border-zinc-800 px-4 py-3 text-center bg-white dark:bg-zinc-900">
            {!checkIn && !checkOut ? (
              <span className="text-sm text-zinc-500 dark:text-zinc-400">
                No dates selected
              </span>
            ) : checkIn && !checkOut ? (
              <span className="text-sm text-zinc-500 dark:text-zinc-400">
                Select your check-out date
              </span>
            ) : (
              <span className="text-sm text-zinc-600 dark:text-zinc-300">
                {formatFooter(checkIn)} - {formatFooter(checkOut)} |{" "}
                {nightCount} night{nightCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
